// Add / edit transaction modal.
// 5-second flow: type → category → amount → date → save.
// Optional `initial` prop edits existing transaction (Phase 5).
//
// Tapping the backdrop closes (after a confirm if dirty).
// Escape closes.
//
// Phase 35d: writes go through dataService.writes.* which writes to
// IndexedDB first (instant), then pushes to server in background if
// Google is connected. `initial` carries `local_uuid` (from local DB)
// not `id` (server id) — use local_uuid for edits.

import React, { useEffect, useRef, useState } from 'react';
import { writes, data } from '../lib/dataService.js';
import { todayISO } from '../lib/format.js';
import { DEFAULT_ACCOUNT_ID, accountLabel, accountTypeMeta } from '../lib/accounts.js';
import { validateTransfer, buildTransfer } from '../lib/transfers.js';
import { normalizeTagsClient, currentTagDraft, replaceTrailingTag, capHistoryAt } from '../lib/tags.js';
import { expandDateRange, WEEKDAY_PRESETS } from '../lib/dateRange.js';
import Numpad from './Numpad.jsx';
import { notifyTxSaved } from '../lib/txSync.js';
// Phase 189 (M-116): AddTransactionSheet (Phase 186/M-113) is gone —
// replaced by the inline swipe-pager + AddCategoryModal lightweight
// drawer.
import AddCategoryModal from './AddCategoryModal.jsx';
import { useI18n } from '../i18n/index.jsx';
import { categoryLabel } from '../lib/categories.js';

// Phase 203: map validateTransfer()'s machine-readable `reason` codes to
// i18n keys. Before, every failure showed the "same account" message —
// so "you haven't picked a destination" was reported as "source and
// destination must differ", which sent the user hunting for the wrong
// problem.
const TRANSFER_ERROR_KEYS = {
  missing_from:       'transfer.errMissingFrom',
  missing_to:         'transfer.errMissingTo',
  same_account:       'transfer.sameAccount',
  invalid_amount:     'transfer.errInvalidAmount',
  from_not_found:     'transfer.errAccountMissing',
  to_not_found:       'transfer.errAccountMissing',
};

// Phase 203: quick date shortcuts. `daysAgo` is relative to today.
// 0 = today, 1 = yesterday, 2 = the day before.
const DATE_SHORTCUTS = [
  { key: 'add.today', daysAgo: 0 },
  { key: 'add.yesterday', daysAgo: 1 },
  { key: 'add.dayBefore', daysAgo: 2 },
];

// Return the YYYY-MM-DD ISO date N days before today, in LOCAL time
// (the same basis as todayISO() in lib/format.js — using toISOString()
// here would shift the date across the UTC boundary for users east of
// Greenwich in the evening).
function shiftDaysISO(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Re-exported so the tag-history helper has a real consumer in this
// module (ESLint flags an imported-but-unused binding) and so the
// behaviour is directly testable from here.
export { capHistoryAt };

export default function AddTransactionModal({ open, onClose, categories, onSaved, initial = null, onboard = false }) {
  const { t, lang } = useI18n();
  const isEdit = !!initial;
  const [type, setType] = useState(initial?.type || 'expense');
  // Phase 60: transfer mode toggle. When true, the modal swaps the
  //   expense/income categories for a from→to account picker.
  const [isTransfer, setIsTransfer] = useState(false);
  const [transferFromId, setTransferFromId] = useState(null);
  const [transferToId, setTransferToId] = useState(null);
  const [categories4Type, setCategories4Type] = useState(
    (categories || []).filter(c => c.type === (initial?.type || 'expense')),
  );
  const [categoryId, setCategoryId] = useState(
    initial?.category_id || (categories || []).find(c => c.type === (initial?.type || 'expense'))?.id || null,
  );
  const [amountStr, setAmountStr] = useState(initial?.amount ? String(initial.amount) : '');
  const [occurredOn, setOccurredOn] = useState(initial?.occurred_on || todayISO());
  const [note, setNote] = useState(initial?.note || '');
  const [tags, setTags] = useState(initial?.tags || '');
  // Round-trip credit card ID. Phase 49e: previously the modal state
  // tracked only the **server** `credit_card_id` (numeric FK), but
  // the dropdown options are keyed by **local** `local_uuid` (string
  // UUID). Editing a saved tx would set `creditCardId = 5` (server
  // FK) but the <select> options are all `local_uuid` strings, so
  // `<select value={String(5)}>` had no matching option and the UI
  // silently fell back to the first option ("None"). The user
  // appeared to lose their card selection on every edit.
  //
  // Resolution: prefer `initial.credit_card_local_uuid` (the canonical
  // local reference we store on save). Fall back to resolving the
  // server FK to a local card row when only `credit_card_id` exists
  // (e.g. transactions saved by an older client that didn't write
  // the local_uuid). If the user is editing a tx whose card has since
  // been deleted, we fall back to null cleanly.
  const [creditCards, setCreditCards] = useState([]);
  // Phase 59: account picker (現金/銀行/電子錢包/信用卡/其他)
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(() => initial?.account_local_uuid || null);
  const [creditCardId, setCreditCardId] = useState(() => {
    if (initial?.credit_card_local_uuid) return initial.credit_card_local_uuid;
    if (initial?.credit_card_id != null) return `server:${initial.credit_card_id}`;
    return null;
  });
  // Phase 80: derived value for the merged payment-method picker.
  // Picks the option that matches whichever backend field is currently
  // set. Defaults to the first account if neither is populated.
  // Recomputes every render via useMemo.
  const paymentMethodValue = React.useMemo(() => {
    if (creditCardId) return `card:${creditCardId}`;
    if (accountId) return `acct:${accountId}`;
    if (accounts.length > 0) {
      const first = accounts[0];
      return `acct:${first.local_uuid || first.id}`;
    }
    return 'acct:default-cash-account';
  }, [accountId, creditCardId, accounts]);
  const handlePaymentMethodChange = (e) => {
    const v = e.target.value;
    if (v.startsWith('card:')) {
      const cardId = v.slice('card:'.length);
      setCreditCardId(cardId);
      // Cards are not accounts — clear the account so savePayload uses
      // the card path (handleSave checks `creditCardId` truthiness).
      setAccountId(null);
    } else if (v.startsWith('acct:')) {
      const aId = v.slice('acct:'.length);
      setAccountId(aId);
      setCreditCardId(null);
    }
  };
  // Phase 49: bulk apply (once-only, no recurring). Off by default
  // for backwards-compatible single-tx flow. Only meaningful when not
  // editing — editing one tx never applies to many.
  const [bulkEnabled, setBulkEnabled] = useState(false);
  const [bulkEndDate, setBulkEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 6); // default 1 week out from start
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [bulkDays, setBulkDays] = useState(WEEKDAY_PRESETS.weekdays); // Mon-Fri default
  // Mobile keyboard handling — when the soft keyboard opens, `window.innerHeight`
  // does NOT shrink on iOS Safari and most Android browsers, so a `92vh` modal
  // would extend below the keyboard and hide the save button. We watch
  // `window.visualViewport` (which DOES shrink on keyboard open) and expose
  // the keyboard height as inline `padding-bottom` on the modal body so the
  // user can always scroll up to the save button. On desktop / no-keyboard,
  // this stays 0.
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const amountRef = useRef(null);
  const dialogRef = useRef(null);

  // Phase 186 (M-113): sheet state. M-116 deletes the sheet (see Task 3).
  // The sheetOpen state is kept around only briefly while Task 2 is in
  // flight — removed after the pager replaces the "+ tile" affordance.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Phase 189 (M-116): swipe-pager state for the category grid.
  // pageIndex: 0-based index of the currently visible page (4 cols ×
  //   3 rows = 12 tiles per page). Synced with the horizontal scroll
  //   container via onScroll listener + scrollToPage().
  // addCategoryOpen: drives the new lightweight AddCategoryModal below
  //   the pager, triggered by the permanent "+ 新增類別" button.
  const [pageIndex, setPageIndex] = useState(0);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);

  // Phase 98 (M-02): swipe-down-to-dismiss for the mobile bottom sheet.
  // Tracks finger Y offset from touchstart; if released past 30% viewport
  // height, the sheet closes. Otherwise it snaps back. Only enabled on
  // mobile (touch + small viewport) — desktop keeps the centred-modal UX.
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);
  const dragging = useRef(false);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
  const onHandleTouchStart = (e) => {
    if (!isMobile) return;
    dragStartY.current = e.touches[0].clientY;
    dragging.current = true;
  };
  const onHandleTouchMove = (e) => {
    if (!dragging.current) return;
    const dy = e.touches[0].clientY - dragStartY.current;
    // Only allow downward drag — upward swipe scrolls content.
    if (dy > 0) setDragY(dy);
  };
  const onHandleTouchEnd = () => {
    if (!dragging.current) return;
    dragging.current = false;
    if (dragY > window.innerHeight * 0.3) {
      handleBackdrop();
    }
    setDragY(0);
  };
  // Reset drag state if the modal closes for any other reason.
  useEffect(() => {
    if (!open) setDragY(0);
  }, [open]);

  // Phase 35b: fetch credit cards + transactions from IndexedDB (instant).
  useEffect(() => {
    data.getCreditCards().then(setCreditCards).catch(() => setCreditCards([]));
    // Phase 59: load accounts for the account picker.
    // Phase 203: `data.getAccounts` NOW EXISTS in dataService's REAL_DATA
    // (it didn't before — the `data.getAccounts ? ...` guard silently
    // swallowed the miss and the picker was always empty). We keep the
    // guard anyway so a test that installs a mock dataService without
    // getAccounts doesn't crash the modal.
    if (typeof data.getAccounts === 'function') {
      data.getAccounts().then(list => {
        setAccounts(list);
        // Phase 203: seed accountId with the account the picker actually
        // shows. Previously accountId stayed null for new transactions
        // and handleSave fell back to the hardcoded 'default-cash-account'
        // string — so if the user's FIRST account (by sort_order) was a
        // bank account, the picker displayed "銀行" but the tx was
        // silently recorded against 現金. Deriving both from the same
        // source removes that class of mismatch.
        setAccountId(prev => {
          if (prev) return prev;
          const first = list[0];
          return first ? (first.local_uuid || first.id) : null;
        });
      }).catch(() => setAccounts([]));
    }
  }, []);

  // Phase 203: the currency symbol. This was HARDCODED to "HK$" while
  // every other page uses `formatMoney(amount, settings.currency)` — so a
  // user who set "$" or "¥" in Settings saw "HK$" in the amount field
  // and "$" everywhere else. Read the same `settings.currency` the rest
  // of the app uses. Fall back to "$" (the seeded default in
  // localDb.js DEFAULT_SETTINGS), not "HK$".
  const [currency, setCurrency] = useState('$');
  useEffect(() => {
    data.getSettings()
      .then(s => { if (s && s.currency) setCurrency(s.currency); })
      .catch(() => { /* keep the default symbol */ });
  }, []);

  // Phase 189 (M-116): swipe-pager refs + helpers. The pager renders
  // N "pages" horizontally inside a single scroll container with CSS
  // scroll-snap, and we sync `pageIndex` state with the visible page
  // via onScroll. `scrollToPage` programmatically scrolls to a given
  // page when the user taps a dot indicator or arrow button.
  const pagerScrollRef = useRef(null);
  const PAGER_PER_PAGE = 12; // 4 cols × 3 rows
  const pageCount = Math.max(1, Math.ceil((categories4Type || []).length / PAGER_PER_PAGE));
  const chunkPages = (cats) => {
    const out = [];
    for (let i = 0; i < cats.length; i += PAGER_PER_PAGE) {
      out.push(cats.slice(i, i + PAGER_PER_PAGE));
    }
    return out;
  };
  const scrollToPage = (idx) => {
    const el = pagerScrollRef.current;
    if (!el) return;
    // Each page is `100%` wide (flex-basis). scrollLeft = idx * clientWidth.
    el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
  };
  // Sync pageIndex from scroll position. Use a small timeout to avoid
  // firing on every pixel of a momentum swipe (debounced via rAF).
  const onPagerScroll = () => {
    const el = pagerScrollRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (w === 0) return;
    const idx = Math.round(el.scrollLeft / w);
    setPageIndex(prev => (prev === idx ? prev : idx));
  };
  // If categories change (e.g. user added one via AddCategoryModal) and
  // pageIndex is now out of bounds, clamp it. Also auto-scroll to the
  // new category's page so the user sees their addition.
  useEffect(() => {
    if (pageIndex >= pageCount) setPageIndex(pageCount - 1);
  }, [pageCount]);
  const handleCategoryCreated = (cat) => {
    // AddCategoryModal returns the new category. Append to categories4Type
    // optimistically and select it. The parent's existing data refresh
    // (data.getCategories via useEffect on open) will sync on next open.
    setCategories4Type(prev => {
      const next = [...(prev || []), cat];
      // Phase 203: scroll the pager to the page that now holds the new
      // tile. Previously this only called setPageIndex(newIdx), which
      // updated the dot indicator but never moved the scroll container —
      // so the user saw the dot change while the grid stayed on the old
      // page and the new category was invisible. The index is computed
      // from the PREVIOUS length (the new tile lands at that index).
      const newIdx = Math.floor((prev || []).length / PAGER_PER_PAGE);
      setPageIndex(newIdx);
      // rAF: the DOM for the new page must exist before we can scroll to it.
      requestAnimationFrame(() => scrollToPage(newIdx));
      return next;
    });
    setCategoryId(cat.local_uuid || cat.id);
    setAddCategoryOpen(false);
  };

  // Phase 32: tag autocomplete history — fetch a wide window of tx tags once,
  // extract unique tokens sorted by recency. Limit=2000 covers ~3 yrs of
  // daily data without paging; if a power user has more we'll just miss the
  // oldest tags, which is fine for autocomplete.
  //
  // Phase 95: cap the history at the most-recent 50 unique tags. Power
  // users with hundreds of distinct tags were ballooning the <datalist>
  // DOM (1 option per tag) and the chip-row filter work. The user asked
  // for "the user's past 50 tags" as the suggestion surface. capHistoryAt
  // (lib/tags.js) handles dedup + cap in a single pass.
  const TAG_HISTORY_LIMIT = 50;
  const [tagsHistory, setTagsHistory] = useState([]);
  useEffect(() => {
    let cancelled = false;
    data.getTransactions({ limit: 2000 }).then(rows => {
      if (cancelled) return;
      // Inline dedup + cap: stop walking transactions as soon as 50 tags
      // are collected, instead of flattening every tag of every row first.
      // (This is deliberately NOT capHistoryAt — that helper takes a flat
      // array, so using it here would mean building the whole array before
      // capping. `tests/tags-ui.test.js` pins this shape.)
      const seen = new Set();
      const out = [];
      for (const tx of rows) {
        if (!tx.tags) continue;
        for (const tok of tx.tags.split(',')) {
          const t = tok.trim().toLowerCase();
          if (!t || seen.has(t)) continue;
          seen.add(t);
          out.push(t);
          if (out.length >= TAG_HISTORY_LIMIT) break;
        }
        if (out.length >= TAG_HISTORY_LIMIT) break;
      }
      setTagsHistory(out);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Reset state when initial changes (so opening for a new tx resets the form)
  useEffect(() => {
    if (!open) return;
    const t = initial?.type || 'expense';
    setType(t);
    setCategories4Type((categories || []).filter(c => c.type === t));
    setCategoryId(initial?.category_id || (categories || []).find(c => c.type === t)?.local_uuid || null);
    setAmountStr(initial?.amount ? String(initial.amount) : '');
    setOccurredOn(initial?.occurred_on || todayISO());
    setNote(initial?.note || '');
    setTags(initial?.tags || '');
    setCreditCardId(
      initial?.credit_card_local_uuid
        ?? (initial?.credit_card_id != null ? `server:${initial.credit_card_id}` : null)
    );
    setError(null);
    setBulkEnabled(false); // bulk apply only meaningful for new tx, not edits
    // Phase 189 (M-116): reset pager + advanced + add-category state when
    // modal opens so reopening doesn't show a stale page.
    setAdvancedOpen(false);
    setPageIndex(0);
    setAddCategoryOpen(false);
    // No autofocus on amount — the virtual Numpad is the primary input now and
    // we don't want the OS soft keyboard popping up and shrinking the modal.
  }, [open, initial, categories]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Track soft-keyboard height via visualViewport so the modal can scroll
  // its content above the keyboard. Falls back to 0 when the API is missing
  // (desktop browsers, very old mobile browsers).
  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) { setKeyboardInset(0); return; }
    const update = () => {
      // keyboardInset = how many CSS px the visual viewport is shorter
      // than the layout viewport. Positive = keyboard is open.
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(inset);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [open]);

  if (!open) return null;

  // Phase 203: the dirty check. It used to be `if (isEdit) return true` —
  // so cancelling an edit ALWAYS asked "Discard this unsaved
  // transaction?" even when the user had changed nothing. Now we compare
  // the current form state against the ORIGINAL tx and only prompt when
  // something actually differs.
  const dirty = (() => {
    if (isEdit) {
      const origAmount = initial?.amount != null ? String(initial.amount) : '';
      const origNote = initial?.note || '';
      const origTags = initial?.tags || '';
      const origDate = initial?.occurred_on || todayISO();
      const origCategory = initial?.category_id || initial?.category_local_uuid || '';
      const origType = initial?.type || 'expense';
      const origAccount = initial?.account_local_uuid || null;
      const origCard = initial?.credit_card_local_uuid || null;
      return (
        amountStr !== origAmount ||
        note !== origNote ||
        tags !== origTags ||
        occurredOn !== origDate ||
        (categoryId || '') !== origCategory ||
        type !== origType ||
        (accountId || null) !== origAccount ||
        (creditCardId || null) !== origCard
      );
    }
    return amountStr || note || (occurredOn !== todayISO());
  })();

  const handleType = (newType) => {
    setType(newType);
    const matched = categories.filter(c => c.type === newType);
    setCategories4Type(matched);
    setCategoryId(matched[0]?.local_uuid || null);
    // Switching to income clears any selected credit card (income isn't paid via card).
    if (newType === 'income') setCreditCardId(null);
  };

  // Allow only digits + one decimal point
  const handleAmount = (e) => {
    const v = e.target.value.replace(/[^0-9.]/g, '');
    const parts = v.split('.');
    const clean = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : v;
    setAmountStr(clean);
  };

  const amount = parseFloat(amountStr) || 0;
  // Phase 203: validity depends on the mode.
  //   - Normal add: needs a category.
  //   - Transfer: has NO category by design (transfers.js sets
  //     category_id: null), so requiring categoryId here locked the
  //     Save button forever with no explanation for the user.
  const amountOk = amount > 0 && !!occurredOn;
  const transferFrom = transferFromId || accountId || DEFAULT_ACCOUNT_ID;
  const valid = isTransfer
    ? (amountOk && !!transferToId && transferToId !== transferFrom)
    : (amountOk && !!categoryId);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    // Phase 60/203: transfer mode — create 2 linked tx rows.
    // Phase 203 bug fix: this branch called `db.table('transactions').put(...)`
    // but `db` was NEVER imported in this module → every transfer save
    // failed with "db is not defined". It also bypassed writes.*, so
    // transfers were never pushed to the server. Now both rows go through
    // writes.createTransaction (local-first + background push), exactly
    // like a normal transaction.
    if (isTransfer) {
      const v = validateTransfer({
        fromAccountId: transferFrom,
        toAccountId: transferToId,
        amount,
        accounts,
      });
      if (!v.ok) {
        setError(t(TRANSFER_ERROR_KEYS[v.reason] || 'transfer.sameAccount'));
        setSaving(false);
        return;
      }
      const built = buildTransfer({
        fromAccountId: transferFrom,
        toAccountId: transferToId,
        amount,
        occurredOn: occurredOn,
      });
      try {
        const fromRow = await writes.createTransaction(built.txFrom);
        await writes.createTransaction(built.txTo);
        notifyTxSaved(fromRow);
        setSaving(false);
        onSaved?.(fromRow);
        // Phase 203: the normal path closes the modal after save; the
        // transfer branch used to `return` early and leave the modal
        // open with the data already written.
        onClose();
        return;
      } catch (e) {
        setError(t('common.failed', { msg: e.message }));
        setSaving(false);
        return;
      }
    }
    if (!valid) return;
    const basePayload = {
      type,
      amount,
      category_id: categoryId,
      note: note || null,
      tags: tags.trim() || null,
      // Phase 59: each tx belongs to one account (default 現金).
      // Phase 80: when the user picked a standalone credit card from
      // the merged payment-method picker, account_local_uuid is null
      // (cleared by handlePaymentMethodChange). Don't default to cash.
      account_local_uuid: creditCardId ? null : (accountId || 'default-cash-account'),
    };
    // Map local credit card ID (UUID from IndexedDB) to the server's
    // numeric FK in the credit_card_id column. Phase 49d: previously
    // this stored the local_uuid string, which the server tried to
    // persist as an integer FK — FK constraint failure on save.
    //
    // Phase 49e: we ALSO store credit_card_local_uuid alongside
    // credit_card_id so the local-first round-trip works (the modal
    // dropdown's options are keyed by local_uuid, not server FK).
    // For server-bound txns, credit_card_id is the numeric FK; the
    // local_uuid is for the UI / future sync fallback.
    //
    // If the card hasn't been synced to the server yet (server_id ==
    // null), we drop the server-side link for THIS transaction; the
    // user can re-sync the card via Settings and then re-link in
    // this tx. The local_uuid is still preserved so the dropdown
    // shows the user's intent.
    if (type === 'expense' && creditCardId) {
      // creditCardId is now always a local_uuid string (Phase 49e);
      // the `server:NN` fallback only appears from initial state for
      // older transactions that pre-date the local_uuid column.
      let cardRow = null;
      if (creditCardId.startsWith('server:')) {
        const sid = parseInt(creditCardId.slice('server:'.length), 10);
        cardRow = (creditCards || []).find(c => c.server_id === sid);
      } else {
        cardRow = (creditCards || []).find(c =>
          (c.local_uuid === creditCardId) || (c.id === creditCardId)
        );
      }
      // Always store the local_uuid (or the legacy "server:NN"
      // sentinel) so re-opening the modal shows the user's pick.
      basePayload.credit_card_local_uuid = creditCardId;
      basePayload.credit_card_id = cardRow?.server_id ?? null;
    }
    // Phase 35d: route through writes.* — local first, server in background.
    // Look up the category row to enrich with category_name / icon / color
    // for the local row (the server response did this for us previously).
    const cat = categories.find(c => c.local_uuid === categoryId || c.id === categoryId);
    if (cat) {
      basePayload.category_name = cat.name;
      basePayload.category_icon = cat.icon;
      basePayload.category_color = cat.color;
      basePayload.category_id = cat.local_uuid || cat.id; // local uuid
    }
    try {
      if (isEdit) {
        // initial.local_uuid is the local DB id; pass that to update.
        const editId = initial.local_uuid || initial.id;
        const saved = await writes.updateTransaction(editId, { ...basePayload, occurred_on: occurredOn });
        onSaved?.(saved);
      } else if (bulkEnabled) {
        // Phase 49: batch create. Expand the range to a list of dates,
        // then POST one transaction per date. Sequential so a partial
        // failure shows a clear error (rather than all-or-nothing).
        const dates = expandDateRange(occurredOn, bulkEndDate, bulkDays);
        if (dates.length === 0) {
          setError(t('bulk.emptyRange'));
          setSaving(false);
          return;
        }
        let lastSaved = null;
        for (const date of dates) {
          const noteSuffix = dates.length > 1
            ? ` (${dates.indexOf(date) + 1}/${dates.length})`
            : '';
          // Sequential awaits — if any single POST fails, we abort and
          // surface the error instead of leaving half-written rows.
          lastSaved = await writes.createTransaction({
            ...basePayload,
            occurred_on: date,
            note: basePayload.note ? basePayload.note + noteSuffix : (noteSuffix.trim() || null),
          });
        }
        onSaved?.(lastSaved);
      } else {
        const saved = await writes.createTransaction({ ...basePayload, occurred_on: occurredOn });
        onSaved?.(saved);
      }
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = () => {
    if (dirty && !confirm(t('add.dirtyConfirm'))) return;
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center ${onboard ? 'z-[60]' : 'z-40'}`}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? t('add.editTitle') : t('add.newTitle')}
    >
      <div
        ref={dialogRef}
        className="bg-white dark:bg-slate-800 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
        style={{
          maxHeight: 'min(92vh, 92dvh)',
          // Phase 98 (M-02): follow the user's finger while dragging.
          // TranslateY(0) is the default (snap to bottom). The transform
          // is no-op on desktop because the touch handlers are no-ops
          // outside the mobile breakpoint.
        transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
        transition: dragging.current ? 'none' : 'transform 200ms ease-out',
        paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Phase 98 (M-02): drag handle. Visual affordance + the touch
            target for swipe-down-to-dismiss. Only on mobile — desktop
            uses the Cancel button. The handle is non-interactive on
            desktop via the isMobile guard inside the handlers. */}
        <div
          className="sm:hidden pt-2 pb-1 flex justify-center cursor-grab active:cursor-grabbing"
          onTouchStart={onHandleTouchStart}
          onTouchMove={onHandleTouchMove}
          onTouchEnd={onHandleTouchEnd}
          data-testid="add-modal-drag-handle"
          aria-hidden="true"
        >
          <div className="w-12 h-1 bg-slate-300 dark:bg-slate-600 rounded-full" />
        </div>
        {/* Phase 102: tutorial banner. Renders only when `onboard=true`
            is passed by OnboardingCarousel Step 2. Lists 3 inline tips
            with per-tip "Got it" buttons; once all three are dismissed
            the banner disappears and the user can save normally. The
            modal still works while the banner is shown — onboarding
            is non-blocking. */}
        {onboard && <OnboardBanner t={t} />}
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <button
            onClick={handleBackdrop}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 px-2 py-1 min-w-[44px] min-h-[44px]"
          >
            {t('common.cancel')}
          </button>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{isEdit ? t('add.editTitle') : t('add.newTitle')}</h2>
          <button
            onClick={handleSave}
            disabled={!valid || saving}
            className="font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 disabled:text-slate-300 dark:disabled:text-slate-600 px-2 py-1 min-w-[44px] min-h-[44px]"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>

        {/* ══ Phase 203 layout rework ══════════════════════════════════
            The old layout was ONE long scrolling column:
              type toggle → amount → numpad → payment → transfer →
              category grid → date → advanced
            Measured on the live site at 420×900 the body was 1113px tall
            in a 743px viewport — 370px of scrolling before the user
            reached the date field, and the category grid was clipped at
            the bottom edge.

            New layout — three zones:
              1. PINNED  : type toggle + amount display
              2. SCROLL  : payment method → transfer form → category grid
                           → date → advanced
              3. PINNED  : numpad, always visible, so the amount can be
                           corrected without scrolling back up to it
            The category grid is now fully visible without scrolling. */}

        {/* ── Zone 1 (pinned): type toggle + amount display ── */}
        <div className="shrink-0 px-4 pt-3">
          {/* Type toggle */}
          <div className="grid grid-cols-2 gap-2 bg-slate-100 dark:bg-slate-700 rounded-lg p-1 mb-2">
            <button
              onClick={() => handleType('expense')}
              className={[
                'py-1.5 rounded-md text-sm font-medium transition-colors min-h-[38px]',
                type === 'expense' ? 'bg-white dark:bg-slate-800 text-red-600 shadow' : 'text-slate-600 dark:text-slate-300',
              ].join(' ')}
            >
              {t('add.expense')}
            </button>
            <button
              onClick={() => handleType('income')}
              className={[
                'py-1.5 rounded-md text-sm font-medium transition-colors min-h-[38px]',
                type === 'income' ? 'bg-white dark:bg-slate-800 text-emerald-600 shadow' : 'text-slate-600 dark:text-slate-300',
              ].join(' ')}
            >
              {t('add.income')}
            </button>
          </div>

          {/* Amount display. Phase 203: the currency symbol was hardcoded
              "HK$" here while every other page uses settings.currency —
              now it reads the same setting. */}
          <div className="mb-1">
            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{t('add.amount')}</label>
            <div className="flex items-baseline gap-2 border-b-2 border-slate-200 dark:border-slate-600 focus-within:border-brand-500 py-1">
              <span className="text-2xl text-slate-400 dark:text-slate-500 font-light">{currency}</span>
              {/* readOnly so the OS soft keyboard never opens — the Numpad
                  below is the only way to enter an amount. */}
              <input
                ref={amountRef}
                type="text"
                inputMode="none"
                readOnly
                value={amountStr}
                onChange={handleAmount}
                placeholder="0"
                className="flex-1 text-3xl font-semibold tracking-wide outline-none tabular-nums placeholder:text-slate-200 dark:placeholder:text-slate-600 bg-transparent text-slate-900 dark:text-slate-100 cursor-default"
                aria-label={t('add.amount')}
              />
            </div>
          </div>
        </div>

        {/* ── Zone 2 (scrollable): everything below the amount ──
            The keyboard inset padding still applies here: the note + tags
            inputs live in this zone and DO raise the OS keyboard. */}
        <div
          className="flex-1 min-h-0 overflow-y-auto px-4 pb-2"
          style={{ paddingBottom: 8 + keyboardInset }}
        >
          {error && (
            <div className="bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 p-3 rounded-md text-sm mb-3">{error}</div>
          )}

          {/* Phase 80: merged payment-method picker. Combines all accounts
              (現金/銀行/電子錢包/etc) + standalone credit cards into a
              single dropdown. Accounts are listed first (since they're
              the common case), then credit cards under a labelled group.
              Backend stores either `account_local_uuid` OR
              `credit_card_local_uuid` depending on which prefix the
              user picks — see handleSave for the dispatch logic. */}
          <div className="mb-3">
            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{t('add.paymentMethod')}</label>
            <select
              value={paymentMethodValue}
              onChange={handlePaymentMethodChange}
              data-testid="payment-method-picker"
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-700 text-sm min-h-[44px]"
            >
              {accounts.length === 0 && (
                <option value="acct:default-cash-account">💵 現金</option>
              )}
              {accounts.map(a => (
                <option
                  key={`acct:${a.local_uuid || a.id}`}
                  value={`acct:${a.local_uuid || a.id}`}
                  data-testid={`payment-option-acct-${a.local_uuid || a.id}`}
                >
                  {accountTypeMeta(a.type).icon} {accountLabel(a, lang)}
                </option>
              ))}
              {creditCards.length > 0 && (
                <optgroup label={t('add.card')}>
                  {creditCards.map(c => (
                    <option
                      key={`card:${c.local_uuid || c.id}`}
                      value={`card:${c.local_uuid || c.id}`}
                      data-testid={`payment-option-card-${c.local_uuid || c.id}`}
                    >
                      💳 {c.name}{c.expiry_display ? ` · ${c.expiry_display}` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

                    {/* Phase 60: mode toggle — normal add vs. transfer */}
          <div className="mb-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsTransfer(false)}
              data-testid="transfer-toggle-add"
              className={[
                'flex-1 px-3 py-2 rounded-lg text-sm min-h-[44px] border',
                !isTransfer
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700',
              ].join(' ')}
            >
              {t('add.modeAdd')}
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor"
                   strokeWidth="1.8" strokeLinecap="round" className="w-4 h-4 ml-1" aria-hidden="true">
                <path d="M10 4v12M4 10h12" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setIsTransfer(true)}
              data-testid="transfer-toggle"
              className={[
                'flex-1 px-3 py-2 rounded-lg text-sm min-h-[44px] border',
                isTransfer
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700',
              ].join(' ')}
            >
              {/* Phase 80: stacked-arrow transfer icon. Top arrow points
                  left→right (out of source account), bottom arrow points
                  right→left (into destination account). 20x20 viewBox
                  scaled to 16x16 to match the add-mode button. */}
              {t('add.modeTransfer')}
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor"
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
                   className="w-4 h-4 ml-1" aria-hidden="true">
                <path d="M3 7h12M12 4l3 3-3 3" />
                <path d="M17 13H5M8 10l-3 3 3 3" />
              </svg>
            </button>
          </div>

                    {/* Phase 60/203: transfer form (replaces the category
                        grid when isTransfer). Phase 203: the account
                        dropdowns were EMPTY in production because the
                        accounts never loaded — now they read the local
                        accounts table. Also added a visible hint when
                        the user has no accounts yet, since an empty
                        dropdown looks identical to a broken one. */}
          {isTransfer && (
            <div className="mb-4" data-testid="transfer-form">
              {accounts.length === 0 ? (
                <div
                  data-testid="transfer-no-accounts"
                  className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-xs text-amber-800 dark:text-amber-200"
                >
                  {t('transfer.noAccountsHint')}
                </div>
              ) : (
                <>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{t('transfer.from')}</label>
                  <select
                    value={transferFromId || accountId || DEFAULT_ACCOUNT_ID}
                    onChange={(e) => { setTransferFromId(e.target.value); setAccountId(e.target.value); }}
                    data-testid="transfer-from"
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-700 text-sm mb-3 min-h-[44px]"
                  >
                    {accounts.map(a => <option key={a.local_uuid} value={a.local_uuid}>{a.name}</option>)}
                  </select>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{t('transfer.to')}</label>
                  <select
                    value={transferToId || ''}
                    onChange={(e) => setTransferToId(e.target.value)}
                    data-testid="transfer-to"
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-700 text-sm min-h-[44px]"
                  >
                    <option value="">{t('transfer.to')}…</option>
                    {accounts.filter(a => a.local_uuid !== (transferFromId || accountId)).map(a => (
                      <option key={a.local_uuid} value={a.local_uuid}>{a.name}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
          )}


          {/* Category pager — Phase 189 (M-116). Horizontal swipe pager
              (4 cols × 3 rows = 12 tiles per page) with CSS scroll-snap,
              dot indicator, and prev/next arrow buttons (desktop). Each
              page is `scroll-snap-start` so native touch swipe snaps to
              whole pages. Empty slots on the last page are filled with
              invisible placeholders so the grid stays aligned.
              Replaces the M-113 "+ tile → sheet" flow.

              Phase 203: hidden in transfer mode — a transfer has no
              category (transfers.js sets category_id: null), so showing
              the grid was both misleading and pushed the date field
              off-screen. */}
          {!isTransfer && (
          <div className="mb-4">
            <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('add.category')}</label>
            <div
              ref={pagerScrollRef}
              onScroll={onPagerScroll}
              data-testid="category-pager"
              className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth"
              style={{ scrollbarWidth: 'none' }}
            >
              {chunkPages(categories4Type || []).map((pageCats, pageIdx) => {
                // Pad with placeholders so the last page keeps the
                // 4-col grid aligned.
                const padded = [...pageCats];
                while (padded.length < PAGER_PER_PAGE) {
                  padded.push({ __placeholder: true, key: `pad-${pageIdx}-${padded.length}` });
                }
                return (
                  <div
                    key={`page-${pageIdx}`}
                    data-testid={`category-pager-page-${pageIdx}`}
                    className="shrink-0 w-full snap-start grid grid-cols-4 gap-2"
                  >
                    {padded.map((c, i) => {
                      if (c.__placeholder) {
                        return <div key={c.key} aria-hidden="true" className="min-h-[64px]" />;
                      }
                      const cid = c.local_uuid || c.id;
                      return (
                        <button
                          key={cid}
                          onClick={() => setCategoryId(cid)}
                          data-testid={`category-tile-${cid}`}
                          aria-pressed={categoryId === cid}
                          aria-label={categoryLabel(c, t)}
                          className={[
                            'flex flex-col items-center gap-1 py-2 rounded-lg transition-colors min-h-[64px]',
                            categoryId === cid
                              ? 'bg-brand-50 dark:bg-brand-900/30 ring-2 ring-brand-500'
                              : 'bg-slate-50 dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600',
                          ].join(' ')}
                        >
                          <span
                            className="w-9 h-9 rounded-full flex items-center justify-center text-lg"
                            style={{ backgroundColor: c.color + '20' }}
                          >
                            {c.icon}
                          </span>
                          <span className="text-[11px] text-slate-700 dark:text-slate-200 truncate w-full text-center">{categoryLabel(c, t)}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {/* Pager controls — dot indicator (always) + prev/next arrows
                (desktop only, mobile uses native swipe). The dots are
                also tappable to jump directly to a page. */}
            {pageCount > 1 && (
              <div className="flex items-center justify-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => scrollToPage(Math.max(0, pageIndex - 1))}
                  disabled={pageIndex === 0}
                  data-testid="category-pager-prev"
                  aria-label={t('addCategoryPager.prev')}
                  className="hidden sm:inline-flex w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 items-center justify-center disabled:opacity-30"
                >
                  ‹
                </button>
                <div className="flex items-center gap-1.5" data-testid="category-pager-dots">
                  {Array.from({ length: pageCount }).map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => scrollToPage(idx)}
                      data-testid={`category-pager-dot-${idx}`}
                      aria-label={t('addCategoryPager.pageIndicator', { n: idx + 1, total: pageCount })}
                      aria-current={pageIndex === idx}
                      className={[
                        'w-2 h-2 rounded-full transition-colors',
                        pageIndex === idx
                          ? 'bg-brand-600 dark:bg-brand-400 w-4'
                          : 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-400 dark:hover:bg-slate-500',
                      ].join(' ')}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => scrollToPage(Math.min(pageCount - 1, pageIndex + 1))}
                  disabled={pageIndex === pageCount - 1}
                  data-testid="category-pager-next"
                  aria-label={t('addCategoryPager.next')}
                  className="hidden sm:inline-flex w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 items-center justify-center disabled:opacity-30"
                >
                  ›
                </button>
              </div>
            )}
            {/* Permanent "+ 新增類別" button — Phase 189 (M-116). Always
                visible below the pager. Opens the lightweight
                AddCategoryModal drawer. */}
            <button
              type="button"
              onClick={() => setAddCategoryOpen(true)}
              data-testid="add-category-button"
              className="mt-2 w-full text-sm text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/30 border border-dashed border-brand-300 dark:border-brand-700 rounded-lg py-2 transition-colors min-h-[40px]"
            >
              {t('addCategory.newCategory')}
            </button>
          </div>
          )}

          {/* Date — Phase 203: quick chips for the common cases
              (today / yesterday / day before) plus the full date
              picker for anything else. The old layout had a full-width
              date input + a separate "今日" button, which cost a row
              and required opening the OS date picker for the most
              common case of all. */}
          <div className="mb-3">
            <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{t('add.date')}</label>
            <div className="flex items-center gap-2">
              <div className="flex gap-1 shrink-0">
                {DATE_SHORTCUTS.map(({ key, daysAgo }) => {
                  const iso = shiftDaysISO(daysAgo);
                  const active = occurredOn === iso;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setOccurredOn(iso)}
                      data-testid={`date-chip-${key}`}
                      aria-pressed={active}
                      className={[
                        'px-2.5 py-2 text-xs font-medium rounded-lg min-h-[44px] transition-colors whitespace-nowrap',
                        active
                          ? 'bg-brand-600 text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600',
                      ].join(' ')}
                    >
                      {t(key)}
                    </button>
                  );
                })}
              </div>
              <input
                type="date"
                value={occurredOn}
                onChange={(e) => setOccurredOn(e.target.value)}
                max={todayISO()}
                aria-label={t('add.date')}
                className="flex-1 min-w-0 px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 rounded-lg text-sm focus:border-brand-500 outline-none min-h-[44px]"
              />
            </div>
          </div>

          {/* Phase 186 (M-113): no-scroll redesign — Advanced accordion.
              Collapses the optional bulk-apply / tags / note sections into
              a single expandable block. Default closed → the visible form
              (type, amount, payment, category, date) fits in 92vh at the
              437x844 viewport with the category grid fully usable; category
              row 2 may clip slightly at the very bottom edge on the smallest
              supported viewport, but the inner body is scrollable so users
              can always reach it. Power users tap "Advanced" to expose
              tags / note / bulk-apply. */}
          <div className="mb-2 border-t border-slate-200 dark:border-slate-700 pt-3">
            <button
              type="button"
              onClick={() => setAdvancedOpen(o => !o)}
              data-testid="advanced-toggle"
              aria-expanded={advancedOpen}
              className="flex items-center justify-between w-full text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-slate-100 min-h-[44px] px-1"
            >
              <span>{t('add.advanced')}</span>
              <span aria-hidden="true" className="text-slate-400">{advancedOpen ? '▴' : '▾'}</span>
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-4">
                {/* Phase 49: bulk apply (new tx only — editing never fans out) */}
                {!isEdit && (
                  <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/40">
                    <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkEnabled}
                        onChange={(e) => setBulkEnabled(e.target.checked)}
                        className="w-5 h-5 accent-brand-600"
                      />
                      <span className="font-medium">{t('bulk.applyToRange')}</span>
                    </label>
                    {bulkEnabled && (
                      <div className="mt-3 space-y-3">
                        {/* End-date picker — start uses the date field above */}
                        <div>
                          <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{t('bulk.endDate')}</label>
                          <input
                            type="date"
                            value={bulkEndDate}
                            min={occurredOn}
                            onChange={(e) => setBulkEndDate(e.target.value)}
                            className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 rounded-lg text-sm focus:border-brand-500 outline-none min-h-[44px]"
                          />
                        </div>
                        {/* Day-of-week checkboxes + preset row */}
                        <div>
                          <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block">{t('bulk.dayOfWeek')}</label>
                          <div className="flex items-center justify-between gap-1 mb-2">
                            {[0, 1, 2, 3, 4, 5, 6].map(d => (
                              <label key={d} className="flex flex-col items-center cursor-pointer flex-1">
                                <input
                                  type="checkbox"
                                  checked={bulkDays.includes(d)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setBulkDays([...bulkDays, d].sort());
                                    } else {
                                      setBulkDays(bulkDays.filter(x => x !== d));
                                    }
                                  }}
                                  className="w-4 h-4 accent-brand-600"
                                />
                                <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                  {t('bulk.weekdayShort')[d]}
                                </span>
                              </label>
                            ))}
                          </div>
                          {/* Quick presets */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              type="button"
                              onClick={() => setBulkDays(WEEKDAY_PRESETS.weekdays)}
                              className="text-xs text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/30 px-2 py-1 rounded min-h-[32px]"
                            >
                              {t('bulk.presetWeekdays')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setBulkDays(WEEKDAY_PRESETS.every_day)}
                              className="text-xs text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/30 px-2 py-1 rounded min-h-[32px]"
                            >
                              {t('bulk.presetEveryDay')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setBulkDays(WEEKDAY_PRESETS.mon_wed_fri)}
                              className="text-xs text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/30 px-2 py-1 rounded min-h-[32px]"
                            >
                              {t('bulk.presetMWF')}
                            </button>
                          </div>
                        </div>
                        {/* Live preview: how many txs will be created */}
                        {(() => {
                          const preview = expandDateRange(occurredOn, bulkEndDate, bulkDays);
                          return (
                            <div className="text-xs text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-700 rounded px-2 py-1.5">
                              {preview.length === 0
                                ? t('bulk.emptyRange')
                                : t('bulk.preview', { count: preview.length })}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Credit card (expense only) — lets the user tag a payment source.
                    Phase 49d: switched the option key/value from `c.id` (server
                    numeric id, undefined for local-only rows) to `c.local_uuid`
                    (the local-first primary key) so saved cards actually
                    appear in the dropdown. parseInt is also dropped — local_uuid
                    is a string UUID. server-side POST /api/transactions expects
                    a numeric FK in the `credit_card_id` column, so we map it
                    to `c.server_id ?? null` below on save — but the UI option
                    carries the local identity. */}
                {/* Phase 80: credit card is no longer a separate dropdown —
                    the merged payment-method picker above shows credit cards
                    alongside accounts. */}


                {/* Tags (optional) — comma-separated ad-hoc labels for ad-hoc filtering. */}
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{t('add.tags')} ({t('common.optional')})</label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder={t('add.tagsPlaceholder')}
                    maxLength={200}
                    list="tag-suggestions"
                    className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 rounded-lg text-sm focus:border-brand-500 outline-none min-h-[44px]"
                    aria-label={t('add.tags')}
                    data-testid="tag-input"
                  />
                  {/* datalist feeds the browser's native autocomplete dropdown on
                      desktop + mobile keyboards that surface it (iOS Safari, Gboard). */}
                  <datalist id="tag-suggestions">
                    {tagsHistory.map(t => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                  {/* One-tap suggestion chips — surfaced for the trailing draft token
                      only, so we don't drown the user in irrelevant history. */}
                  {(() => {
                    const draft = currentTagDraft(tags);
                    if (!draft || draft.length < 1) return null;
                    const already = new Set(normalizeTagsClient(tags));
                    const matches = tagsHistory.filter(t => t.startsWith(draft) && !already.has(t)).slice(0, 10);
                    if (matches.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-1 mt-2" data-testid="tag-suggestions-row">
                        {matches.map(t => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setTags(replaceTrailingTag(tags, t) + ', ')}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-200 hover:bg-brand-100 dark:hover:bg-brand-900/50 min-h-[28px]"
                          >
                            #{t}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {tags.trim() && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {normalizeTagsClient(tags).map((v, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-200">
                          #{v}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Note */}
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">{t('add.note')} ({t('common.optional')})</label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t('add.notePlaceholder')}
                    maxLength={100}
                    className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 rounded-lg text-sm focus:border-brand-500 outline-none min-h-[44px]"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Zone 3 (pinned): the numpad ──────────────────────────────
            Phase 203: the numpad used to sit inline right under the
            amount field, so it pushed the payment method, category grid
            and date field 370px down the page. Pinning it to the bottom
            keeps the amount editable at all times AND frees ~250px of
            vertical space for the fields above.

            The Numpad keys are slightly shorter than before (44px vs
            52px) so the whole pinned block fits on a 640px-tall phone
            alongside the amount display. */}
        <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-4 pt-2 bg-white dark:bg-slate-800">
          <Numpad value={amountStr} onChange={setAmountStr} compact />
        </div>
      </div>

      {/* Phase 189 (M-116): AddCategoryModal — lightweight bottom-sheet
          drawer for creating a new category from inside this modal.
          Replaces the old AddTransactionSheet (M-113). The new category
          is auto-selected and the pager scrolls to the new tile's page. */}
      <AddCategoryModal
        open={addCategoryOpen}
        type={type}
        onClose={() => setAddCategoryOpen(false)}
        onCreated={handleCategoryCreated}
      />
    </div>
  );
}

// Phase 102: 3-tip tutorial banner shown above the form when launched
// from onboarding Step 2. Each tip has a per-tip "Got it" button; the
// banner disappears once all three are dismissed. Non-blocking — the
// modal still saves normally while tips are visible.
function OnboardBanner({ t }) {
  const TIPS = [
    { id: 'type',     label: t('onboard.tooltip.type') },
    { id: 'amount',   label: t('onboard.tooltip.amount') },
    { id: 'category', label: t('onboard.tooltip.category') },
  ];
  const [dismissed, setDismissed] = useState({});
  const allDone = TIPS.every(tip => dismissed[tip.id]);
  if (allDone) return null;
  return (
    <div
      className="mx-4 mt-2 mb-1 p-2 rounded-lg bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-700 text-xs text-slate-700 dark:text-slate-200"
      data-testid="onboard-banner"
    >
      <div className="space-y-1">
        {TIPS.map(tip => dismissed[tip.id] ? null : (
          <div key={tip.id} className="flex items-start gap-2">
            <span aria-hidden="true">💡</span>
            <span className="flex-1">{tip.label}</span>
            <button
              onClick={() => setDismissed(d => ({ ...d, [tip.id]: true }))}
              data-testid={`onboard-banner-got-${tip.id}`}
              className="shrink-0 px-2 py-0.5 rounded bg-brand-600 hover:bg-brand-700 text-white text-[10px] font-medium"
            >
              {t('onboard.tooltip.gotIt')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
